import { sumLines } from './load.js';

/* --------------------------------------------------------------------------
   Urval på radnivå: artikelgrupp och leverantör

   Butik, kassa och kassör hör till kvittot. Artikelgrupp och leverantör hör
   till varje rad — ett kvitto kan innehålla en kabel från en leverantör och en
   telefon från en annan. Att behålla hela kvitton som "innehåller" gruppen
   skulle räkna telefonen som adapterförsäljning. Därför snävas raderna in och
   kvittots belopp räknas om; kvitton utan någon matchande rad faller bort.

   Följden för nyckeltalen:
     • omsättning, vinst, marginal och artiklar gäller bara urvalet
     • antal köp är antal kvitton som innehöll urvalet
     • betalningar, presentkort, bonuscheckar och öresavrundning gäller hela
       kvittot och går inte att fördela per rad. De nollas och redovisas inte.
   -------------------------------------------------------------------------- */

/** Värde i väljarna för rader som saknar artikelgrupp eller leverantör. */
export const NONE = '__ingen__';
/** Avgränsare mellan nivåerna i articleGroupTree, "Försäljning||Adapter". */
export const GROUP_SEP = '||';

/** Läsbar etikett för en gruppsökväg: "Försäljning › Adapter". */
/* En grupp kan ha egna artiklar och samtidigt undergrupper: "Livsmedel"
   och "Livsmedel||Läsk". I trädet får de egna artiklarna då ett eget val,
   "Livsmedel||=" — "utan undergrupp". Annars betyder "alla undergrupper
   valda" inte "hela gruppen", och kryssar man bara Läsk följer Livsmedels
   egna artiklar med. Värdet matchar bara rader som ligger direkt i gruppen. */
export const EXACT = '=';
const EXACT_SUFFIX = GROUP_SEP + EXACT;

/** Gruppen ett "utan undergrupp"-val gäller, eller null. */
export function exactGroup(path){
  return path.endsWith(EXACT_SUFFIX) ? path.slice(0, -EXACT_SUFFIX.length) : null;
}

export function groupLabel(path){
  if(!path) return 'Saknar artikelgrupp';
  const exact = exactGroup(path);
  if(exact !== null) return groupLabel(exact) + ' › utan undergrupp';
  return path.split(GROUP_SEP).join(' › ');
}

/**
 * Returnerar en funktion som avgör om en rad ingår i urvalet, eller null när
 * inget radurval är valt.
 *
 * f.groups och f.suppliers är listor. Inom en lista räcker det att en rad
 * matchar något värde; mellan listorna måste raden matcha båda. En grupp
 * matchar sig själv och alla sina undergrupper. NONE väljer rader som saknar
 * grupp respektive leverantör.
 *
 * En kampanjrabatt som inte gick att fördela på artiklarna (unallocated) hör
 * till kvittot, som betalningar och öresavrundning, och ingår aldrig i ett
 * radurval. Annars skulle "Saknar artikelgrupp" visa en rabatt som om den
 * vore en artikel att rätta.
 */
export function linePredicate(f){
  const groups = f.groups || [], suppliers = f.suppliers || [];
  if(!groups.length && !suppliers.length) return null;

  const noGroup  = groups.includes(NONE);
  const prefixes = groups.filter(g => g !== NONE);
  const noSupp   = suppliers.includes(NONE);
  const supp     = new Set(suppliers.filter(v => v !== NONE));

  const matchers = prefixes.map(p => {
    const exact = exactGroup(p);
    return exact !== null
      ? g => g === exact
      : g => g === p || g.startsWith(p + GROUP_SEP);
  });
  const inGroup = ln => ln.group ? matchers.some(m => m(ln.group)) : noGroup;
  const inSupp = ln => ln.supplier ? supp.has(ln.supplier) : noSupp;

  return ln => !ln.unallocated && (!groups.length || inGroup(ln)) && (!suppliers.length || inSupp(ln));
}

/** Kopia av kvittot med bara de rader som ingår i urvalet. */
export function narrowReceipt(r, keep){
  const n = {
    ...r,
    orig: r.orig || r,   // hela kvittot, för korganalysen (calc/basket.js)
    lines: r.lines.filter(keep),
    paid: 0, payments: [], cards: [],
    giftCardBooked: 0, giftCardFace: 0, giftCardCount: 0,
    bonusChecks: 0, bonusCount: 0, roundOff: 0
  };
  sumLines(n);
  return n;
}

/** Alla gruppsökvägar i filen inklusive överordnade nivåer, i trädordning. */
export function groupPaths(receipts){
  const set = new Set();
  const direct = new Set();   // grupper som har egna artiklar
  let none = false;
  for(const r of receipts) for(const ln of r.lines){
    if(ln.unallocated) continue;
    if(!ln.group){ none = true; continue; }
    direct.add(ln.group);
    const parts = ln.group.split(GROUP_SEP);
    for(let i = 1; i <= parts.length; i++) set.add(parts.slice(0, i).join(GROUP_SEP));
  }
  // Egna artiklar i en grupp som också har undergrupper får ett eget val
  for(const g of direct){
    if([...set].some(p => p.startsWith(g + GROUP_SEP))) set.add(g + EXACT_SUFFIX);
  }
  // Jämför nivå för nivå, så att en grupp alltid står direkt före sina
  // undergrupper, och "utan undergrupp" först bland dem
  const paths = [...set].sort((a,b) => {
    const pa = a.split(GROUP_SEP), pb = b.split(GROUP_SEP);
    for(let i = 0; i < Math.min(pa.length, pb.length); i++){
      if(pa[i] === pb[i]) continue;
      if(pa[i] === EXACT) return -1;
      if(pb[i] === EXACT) return 1;
      const c = pa[i].localeCompare(pb[i], 'sv');
      if(c) return c;
    }
    return pa.length - pb.length;
  });
  if(none) paths.push(NONE);
  return paths;
}

export function supplierValues(receipts){
  const set = new Set();
  let none = false;
  for(const r of receipts) for(const ln of r.lines){
    if(ln.unallocated) continue;
    if(ln.supplier) set.add(ln.supplier); else none = true;
  }
  const vals = [...set].sort((a,b) => a.localeCompare(b, 'sv', {numeric:true}));
  if(none) vals.push(NONE);
  return vals;
}
